import {Location} from 'react-router';

import {ReportName} from 'common/report/constants';
import {queryApiUrl} from 'common/report/queries/utils';
import {QueryReason, QueryTrackingProps} from 'common/report/util/query-tracking/types';
import {apiClient} from 'common/util/api-client';
import {ResponseError} from 'common/util/shared-client';

import {reporter} from '../error-reporting';
import {BaseQueryErrorType, BaseQueryResultsType, ParamsAndResults} from '../types';
import {CurrentProject} from '../model-resources/projects';
import {beforeQuery} from './query-lifecycles';

// intended to support insights, funnels, retention, and flows
// this function is currently reused in dashboards to enforce a common querying codepath,
// and may be further consolidated by using QueryManager in dashboards as well
export function executeReportQuery({
  endpoint: originalEndpoint,
  queryParams,
  abortSignal,
  body: originalBody = {},
  location,
  // dashboards currently only handles errors based on status code
  // however, in reports we may want to use more detailed information provided from the server
  throwOnInvalidStatus = true,
}: {
  endpoint: string;
  queryParams?: Record<string, string>;
  abortSignal?: AbortSignal;
  body?: any;
  // dashboards currently only handles errors based on status code
  // however, in reports we may want to use more detailed information provided from the server
  throwOnInvalidStatus?: boolean;
  location: Location;
}): Promise<Response> {
  // Run life cycle hooks before making the query request
  // we use this hook to maintain all necessary inline payload transformations
  const {endpoint, urlParams, body} = beforeQuery({
    endpoint: originalEndpoint,
    urlParams: new URLSearchParams(queryParams ?? {}),
    body: originalBody,
    location,
  });
  // using apiClient.fetch because apiClient.post() doesn't support query api format
  // eslint-disable-next-line local-rules/deprecated-member-access
  return apiClient.fetch(queryApiUrl(`${endpoint}?${urlParams.toString()}`), {
    headers: {'Content-Type': `application/json; charset=UTF-8`},
    method: `POST`,
    body: JSON.stringify(body),
    throwOnInvalidStatus,
    abortSignal,
  });
}

export const queryModule = {executeReportQuery};

export abstract class QueryManager<
  BookmarkParamsType,
  QueryResultsType extends BaseQueryResultsType = BaseQueryResultsType,
  QueryErrorType extends BaseQueryErrorType = BaseQueryErrorType
> {
  abstract reportName: ReportName;
  abstract endpoint: string;
  protected abstract getExtraPostParams({
    bookmarkParams,
    currentProject,
    location,
  }: {
    bookmarkParams: BookmarkParamsType;
    currentProject: CurrentProject;
    location: Location;
  });

  public projectId: number;
  public workspaceId: number;

  private queryController: AbortController = new AbortController();

  constructor({projectId, workspaceId}: {projectId: number; workspaceId?: number}) {
    this.projectId = projectId;
    this.workspaceId = workspaceId;
  }

  protected getEndpoint(_bookmarkParams: BookmarkParamsType): string {
    return this.endpoint;
  }

  // optionally implemented to transform a query error into a report-specific type
  public handleQueryError(err: BaseQueryErrorType): QueryErrorType {
    return err as QueryErrorType;
  }

  // not all reports support csv export, so this is optionally implemented
  public getExtraCsvPostParams(_bookmarkParams: BookmarkParamsType, _currentProject: CurrentProject) {
    return {};
  }

  private get queryParams() {
    return {
      ...(this.workspaceId ? {workspace_id: this.workspaceId.toString()} : {}),
      project_id: this.projectId.toString(),
    };
  }

  public async csvExportQuery({
    bookmarkParams,
    currentProject,
    location,
    useQueryCache = true,
  }: {
    bookmarkParams: BookmarkParamsType;
    currentProject: CurrentProject;
    location: Location;
    useQueryCache?: boolean;
  }): Promise<{
    result: string;
    filename?: string;
  }> {
    const postPayload = {
      bookmark: bookmarkParams,
      use_query_cache: useQueryCache,
      ...this.getExtraCsvPostParams(bookmarkParams, currentProject),
      format: `csv`,
    };

    // no need to abort pending csv queries
    const result = await queryModule.executeReportQuery({
      endpoint: this.getEndpoint(bookmarkParams),
      queryParams: this.queryParams,
      body: postPayload,
      abortSignal: null,
      throwOnInvalidStatus: true,
      location,
    });
    const csvData = await result.text();
    const filename = result.headers.get(`Content-Disposition`)?.match(/filename="([^"]+)"/)?.[1];
    return {
      result: csvData,
      filename,
    };
  }

  public abortPendingQuery() {
    this.queryController.abort();
    this.queryController = new AbortController();
  }

  public async query({
    bookmarkParams,
    currentProject,
    queryReason,
    hasUnsavedBookmarkChanges,
    useQuerySampling,
    location,
    bookmarkId,
    useQueryCache = true,
  }: {
    bookmarkParams: BookmarkParamsType;
    currentProject: CurrentProject;
    queryReason: QueryReason;
    hasUnsavedBookmarkChanges: boolean;
    useQuerySampling: boolean;
    location: Location;
    bookmarkId?: null | number;
    useQueryCache?: boolean;
  }): Promise<QueryResultsType | QueryErrorType> {
    this.abortPendingQuery();

    const trackingProps: QueryTrackingProps = {
      is_main_query_for_report: true,
      report_name: this.reportName,
      bookmark_id: bookmarkId,
      has_unsaved_changes: hasUnsavedBookmarkChanges,
      query_reason: queryReason,
    };

    const postPayload = {
      tracking_props: trackingProps,
      bookmark: bookmarkParams,
      ...this.getExtraPostParams({bookmarkParams, location, currentProject}),
      use_query_cache: useQueryCache,
      use_query_sampling: useQuerySampling,
    };

    try {
      const response = await queryModule.executeReportQuery({
        endpoint: this.getEndpoint(bookmarkParams),
        queryParams: this.queryParams,
        body: postPayload,
        abortSignal: this.queryController.signal,
        throwOnInvalidStatus: false,
        location,
      });

      const results = await response.json();

      // query-api responded with a 4XX - get any detailed information from the response
      if (results.error) {
        const error: BaseQueryErrorType = {
          status: response.status,
          error: results.error,
          data: results.data,
          ...results,
        };

        return this.handleQueryError(error);
      }

      // TODO: run handleResults here when QueryManager is used for query promises passed into ReportEditor

      return results;
    } catch (err) {
      if (err instanceof ResponseError) {
        // apiClient will only throw here if response.status >=500
        return this.handleQueryError({error: err.getMessage(), status: err.response.status});
      } else if (err.name === `AbortError`) {
        return null;
      } else {
        // Something went wrong with the frontend processing the result
        const error = err as Error;
        reporter.error(`Error processing ${this.getEndpoint(bookmarkParams)} query`, err);
        return this.handleQueryError({error: error.stack, status: null});
      }
    }
  }

  /**
   * Optional method to override and transform bookmark params or results when query is returned.
   * Sometimes we need to change bookmark settings based on results and vice-versa
   * @param paramsAndResults - current bookmark params and results returned from query
   * @returns params and results, optionally modified
   */
  handleResults(
    paramsAndResults: ParamsAndResults<BookmarkParamsType, QueryResultsType>,
  ): ParamsAndResults<BookmarkParamsType, QueryResultsType> {
    return paramsAndResults;
  }
}
